﻿import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../services/apiClient';
import { motion, AnimatePresence } from 'motion/react';
import { Send, MessageSquare, Cpu, Globe, Zap, Loader2, User as UserIcon, Settings, Eye, Camera, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { LocalAgentSphere } from './LocalAgentSphere';
import { socketService } from '@/services/socketService';
import { useTTS } from '@/hooks/useTTS';
import { useModuleData } from '@/hooks/useModuleData';
import { GlassCard } from './SharedUI';
import { useApp } from '../contexts/AppContext';
import { useVoiceCall } from '@/hooks/useVoiceCall';
import { useSocket } from '@/hooks/useSocket';
import { toast } from 'sonner';

export function UnifiedAgent({ t, user, onEnterSanctuary }: { t: any; user: any; onEnterSanctuary?: () => void }) {
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const { user: appUser } = useApp();
  const [isVisionActive, setIsVisionActive] = useState(false);
  const [visionData, setVisionData] = useState<string[]>([]);
  const [founderVision, setFounderVision] = useState('');
  const [isFounderEditing, setIsFounderEditing] = useState(false);
  
  const socket = useSocket();
  const { callState, audioLevel, startCall, endCall, transcript } = useVoiceCall({
    socket,
    onTranscript: (text, isFinal) => {
      if (isFinal) {
        const userMsg = {
          id: Date.now().toString(),
          text,
          userName: user?.displayName || ui('用户', 'User'),
          timestamp: new Date().toISOString(),
          type: 'user'
        };
        setMessages(prev => [...prev, userMsg]);
      }
    },
    onResponse: (text) => {
      const agentMsg = {
        id: Date.now().toString(),
        text,
        userName: '小枫',
        timestamp: new Date().toISOString(),
        type: 'agent'
      };
      setMessages(prev => [...prev, agentMsg]);
    }
  });

  const { speak, stop, isSpeaking } = useTTS();
  const { data: agents, error: agentsError } = useModuleData<any[]>('/api/agents');
  const agentConfig = agents?.[0];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isVoiceMode, setIsVoiceMode] = useState(false);

  const [isPrivateMode, setIsPrivateMode] = useState(false);

  useEffect(() => {
    if (!socket) return;

    const handleAgentResponse = (data: { text: string; agentName: string }) => {
      const agentMsg = {
        id: Date.now().toString(),
        text: data.text,
        userName: data.agentName,
        timestamp: new Date().toISOString(),
        type: 'agent'
      };
      setMessages(prev => [...prev, agentMsg]);
      
      // Only speak if we are in voice mode or it was a voice trigger
      if (isVoiceMode) {
        speak(data.text);
      }
    };

    const handleStatus = (data: { status: string }) => {
      setIsTyping(data.status === "thinking");
    };

    const handleError = (data: { message: string }) => {
      console.error('[UnifiedAgent] Socket Agent Error:', data.message);
      setIsTyping(false);
    };

    const handleProactive = (data: { type: string; message: string; agentName?: string; timestamp: string }) => {
      if (data.type === 'greeting') {
        const greetingMsg = {
          id: `greeting-${data.timestamp}`,
          text: data.message,
          userName: data.agentName || '小枫',
          timestamp: data.timestamp,
          type: 'agent'
        };
        setMessages(prev => [...prev, greetingMsg]);
        if (isVoiceMode) speak(data.message);
      }
    };

    socket.on("agent:response", handleAgentResponse);
    socket.on("agent:status", handleStatus);
    socket.on("agent:error", handleError);
    socket.on("agent:proactive", handleProactive);

    return () => {
      socket.off("agent:response", handleAgentResponse);
      socket.off("agent:status", handleStatus);
      socket.off("agent:error", handleError);
      socket.off("agent:proactive", handleProactive);
    };
  }, [socket, speak, isVoiceMode]);

  const fetchInteractions = async () => {
    try {
      const res = await apiFetch('/api/interactions');
      if (res.ok) {
        const data = await res.json();
        setMessages(Array.isArray(data) ? data.map((i: any) => ({
          id: i.id,
          text: i.content,
          userName: i.role === 'user' ? (user?.displayName || ui('用户', 'User')) : (agentConfig?.name || '小枫'),
          timestamp: i.timestamp,
          type: i.role === 'user' ? 'user' : 'agent'
        })) : []);
      }
    } catch (error) {
      console.error('Error fetching interactions:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchInteractions();
      fetchFounderVision();
    } else {
      setMessages([]);
    }
  }, [user]);

  useEffect(() => {
    if (agentsError) toast.error(t.failedToLoadAgentConfig || '加载智能体配置失败');
  }, [agentsError]);

  const fetchFounderVision = async () => {
    try {
      const res = await apiFetch('/api/founder/vision');
      if (res.ok) {
        const data = await res.json();
        setFounderVision(data.vision);
      }
    } catch (err) {
      console.error('Error fetching founder vision:', err);
    }
  };

  const updateFounderVision = async () => {
    try {
      const res = await apiFetch('/api/founder/vision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vision: founderVision })
      });
      if (res.ok) {
        setIsFounderEditing(false);
      }
    } catch (err) {
      console.error('Error updating founder vision:', err);
    }
  };

  const toggleVision = () => {
    setIsVisionActive(!isVisionActive);
    if (!isVisionActive) {
      // Query active device capabilities from the mesh
      apiFetch('/api/devices')
        .then(res => res.json())
        .then(data => {
          const ctx = data.sensoryContext;
          if (ctx && ctx.deviceCount > 0) {
            const caps: string[] = [];
            if (ctx.hasAudio) caps.push(ui('音频输入', 'Audio Input'));
            if (ctx.hasVideo) caps.push(ui('摄像头', 'Camera'));
            if (ctx.hasSpatial) caps.push(ui('空间追踪', 'Spatial Tracking'));
            if (ctx.hasHaptic) caps.push(ui('触觉反馈', 'Haptic Feedback'));
            if (ctx.hasHolographic) caps.push(ui('全息输出', 'Holographic Output'));
            setVisionData(caps.length > 0 ? caps : [ui('没有活跃传感器', 'No active sensors')]);
          } else {
            setVisionData([ui('没有连接设备', 'No devices connected')]);
          }
        })
        .catch(() => setVisionData([ui('传感器 API 不可用', 'Sensor API unavailable')]));
    } else {
      setVisionData([]);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async (e?: React.FormEvent, text?: string, isVoice: boolean = false) => {
    if (e) e.preventDefault();
    const messageText = text || newMessage;
    if (!messageText.trim() || !user) return;

    setIsVoiceMode(isVoice);
    
    // If typing, stop any ongoing speech
    if (!isVoice) {
      stop();
    }

    const userMsg = {
      id: Date.now().toString(),
      text: messageText,
      userName: user.displayName || user.username || ui('匿名用户', 'Anonymous'),
      timestamp: new Date().toISOString(),
      type: 'user'
    };

    setMessages(prev => [...prev, userMsg]);
    setNewMessage('');
    
    if (socket) {
      socket.emit("agent:chat", {
        text: messageText,
        history: messages.map(m => ({
          role: m.type === 'user' ? 'user' : 'assistant',
          content: m.text
        })),
      personalityId: 'xiaofeng'
    });
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-12">
      {/* Top Row: Holographic Module & Founder Vision */}
      <div className="grid md:grid-cols-2 gap-8">
        {/* Left: Holographic Module (Carrier Dock & Sensing) */}
        <GlassCard className="p-8 rounded-[2.5rem] space-y-6" hoverEffect={false}>
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold tracking-tighter flex items-center gap-2">
              <Cpu size={20} className={isPrivateMode ? "text-celestial-saturn" : "text-celestial-glow animate-pulse"} />
              {isPrivateMode ? ui('物理隔离', 'Physical Isolation') : ui('神经载体', 'Neural Carrier')}
            </h3>
            <div className="flex gap-2">
              <Button 
                onClick={toggleVision}
                className={`rounded-full px-3 h-7 text-[12px] font-bold uppercase tracking-widest ${
                  isVisionActive ? 'bg-celestial-saturn text-black' : 'bg-white/5 text-white/40'
                }`}
              >
                {ui('传感器', 'Sensors')}
              </Button>
              <Button 
                onClick={() => setIsPrivateMode(!isPrivateMode)}
                className={`rounded-full px-3 h-7 text-[12px] font-bold uppercase tracking-widest ${
                  isPrivateMode ? 'bg-celestial-saturn text-black' : 'bg-white/5 text-white/40'
                }`}
              >
                {isPrivateMode ? ui('上线', 'Online') : ui('断开开关', 'Kill-Switch')}
              </Button>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-2xl border border-white/5">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full status-pulse ${isPrivateMode ? 'bg-celestial-saturn' : 'bg-celestial-glow'}`} />
                <span className="text-xs font-bold uppercase tracking-widest text-white/60">
                  {isPrivateMode ? ui('本地 NPU 活跃', 'Local NPU Active') : ui('网络已同步', 'Mesh Synced')}
                </span>
              </div>
              <span className="text-[12px] font-mono text-white/45">v2.0-Alpha</span>
            </div>

            <div className="flex flex-wrap gap-2 min-h-[32px]">
              {isVisionActive ? (
                visionData.map((obj, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="px-2 py-1 bg-white/5 border border-white/10 rounded-lg text-xs font-medium flex items-center gap-2"
                  >
                    <div className="w-1 h-1 rounded-full bg-celestial-saturn" />
                    {obj}
                  </motion.div>
                ))
              ) : (
                <p className="text-xs text-white/45 italic">{ui('边缘传感器待命中...', 'Edge sensors on standby...')}</p>
              )}
            </div>
          </div>
        </GlassCard>

        {/* Right: Founder's Vision */}
        <GlassCard className="p-8 rounded-[2.5rem] space-y-6" hoverEffect={false}>
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold tracking-tighter flex items-center gap-2">
              <Zap size={20} className="text-celestial-mars" />
              {t.founderVision || ui('创始愿景', "Founder's Vision")}
            </h3>
            {user?.role === 'admin' && (
              <Button 
                onClick={() => isFounderEditing ? updateFounderVision() : setIsFounderEditing(true)}
                className="rounded-full px-4 h-8 text-xs font-bold uppercase tracking-widest bg-white/5 text-white/40 hover:bg-white/10"
              >
                {isFounderEditing ? (t.updateVision || ui('更新愿景', 'Update Vision')) : ui('编辑愿景', 'Edit Vision')}
              </Button>
            )}
          </div>
          
          {isFounderEditing ? (
            <textarea
              value={founderVision}
              onChange={(e) => setFounderVision(e.target.value)}
              className="w-full h-24 bg-black/20 border border-white/10 rounded-2xl p-4 text-sm text-white/80 focus:outline-none focus:border-celestial-saturn/50 resize-none font-mono"
            />
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-white/60 leading-relaxed italic">
                "{founderVision || ui('小枫 旨在构建一个去中心化的智能协议...', '小枫 aims to build a decentralized intelligence protocol...')}"
              </p>
              <Button 
                onClick={onEnterSanctuary}
                className="w-full py-6 rounded-2xl bg-celestial-saturn/10 border border-celestial-saturn/30 text-celestial-saturn font-bold hover:bg-celestial-saturn hover:text-black transition-all flex items-center justify-center gap-2 group"
              >
                <Sparkles size={18} className="group-hover:animate-spin" />
                {t.enterSanctuary || ui('进入创始人圣所', 'Enter Founder Sanctuary')}
              </Button>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Top Section: Voice & Visual Agent */}
      <section className="relative">
        <div className="text-center space-y-4 mb-8">
          <h2 className="text-4xl font-bold tracking-tighter glow-text">
            {ui('小枫 核心智能体', '小枫 Core Agent')}
          </h2>
          <p className="text-white/40 max-w-xl mx-auto italic">
            "{t.holographicEntranceDesc}"
          </p>
        </div>
        
        <div className="flex flex-col lg:flex-row items-center justify-center gap-12">
          <div className="w-full lg:w-1/2">
            <LocalAgentSphere 
              t={t} 
              callState={callState}
              audioLevel={audioLevel}
              onStartCall={() => startCall(undefined, 'xiaofeng', 'xiaofeng')}
              onEndCall={endCall}
            />
          </div>

          {/* Message Board (Simplified Chat) */}
          <div className="w-full lg:w-1/2 flex flex-col h-[500px] glass rounded-[2.5rem] border-white/10 overflow-hidden relative">
            {/* Real-time Overlay for Transcript */}
            <AnimatePresence>
              {callState !== 'idle' && transcript && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-x-0 top-20 z-50 flex justify-center px-4 pointer-events-none"
                >
                  <div className="inline-flex max-w-full items-start justify-center gap-3 rounded-2xl bg-celestial-saturn px-5 py-3 text-center text-sm font-bold text-black shadow-2xl">
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-black animate-pulse" />
                    <span className="min-w-0 whitespace-normal break-words leading-relaxed">{transcript}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div className="p-6 border-b border-white/5 flex items-center justify-between bg-white/5">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${isSpeaking ? 'bg-celestial-mars animate-ping' : 'bg-celestial-saturn animate-pulse'}`} />
                <span className="text-xs font-bold uppercase tracking-widest text-white/60">{t.realTimeNode || ui('实时节点', 'Real-time Node')}</span>
                {isSpeaking && (
                  <Button 
                    onClick={stop}
                    className="h-6 px-2 text-xs bg-red-500/20 text-red-500 hover:bg-red-500/40 rounded-full border border-red-500/20"
                  >
                    {t.stopSpeaking || ui('停止', 'STOP')}
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="text-xs text-white/40 font-mono uppercase">
                  {t.founderMode || ui('创始人模式', 'Founder Mode')}
                </div>
              </div>
            </div>

            <div 
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-hide"
            >
              {messages.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-20">
                  <MessageSquare size={48} />
                  <p className="text-sm">{ui('尚未有交互记录', 'No interactions yet')}<br />{ui('开始与您的本地智能体对话', 'Start talking with your local agent')}</p>
                </div>
              )}
              <AnimatePresence initial={false}>
                {messages.map((msg) => (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex flex-col ${msg.type === 'agent' ? 'items-start' : 'items-end'}`}
                  >
                    <div className={`max-w-[85%] p-4 rounded-2xl text-sm ${
                      msg.type === 'agent' 
                        ? 'bg-celestial-saturn/10 text-celestial-saturn border border-celestial-saturn/20 rounded-tl-none' 
                        : 'bg-white/5 text-white/80 border border-white/10 rounded-tr-none'
                    }`}>
                      {msg.text}
                    </div>
                    <span className="text-[12px] uppercase tracking-tighter opacity-30 mt-1 px-2">
                      {msg.userName} • {new Date(msg.timestamp).toLocaleTimeString(isZh ? 'zh-CN' : undefined, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
              {isTyping && (
                <div className="flex gap-1 items-center text-celestial-saturn/40 text-xs">
                  <Loader2 size={12} className="animate-spin" />
                  {ui('智能体正在思考...', 'Agent is thinking...')}
                </div>
              )}
            </div>

            <div className="p-4 bg-white/5 border-t border-white/5">
              <form onSubmit={handleSendMessage} className="relative flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={ui('输入指令或留言...', 'Type a command or message...')}
                  className="bg-black/20 border-white/10 rounded-xl focus-visible:ring-celestial-saturn/50"
                />
                <Button 
                  type="submit" 
                  disabled={isTyping}
                  className="bg-celestial-saturn text-black rounded-xl px-4 hover:scale-105 transition-transform"
                >
                  <Send size={18} />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Stats / Info Row */}
      <StatsRow socket={socket} t={t} />
    </div>
  );
}

function StatCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) {
  return (
    <GlassCard className="p-6 rounded-3xl flex items-center gap-4" hoverEffect={false}>
      <div className={`p-3 rounded-2xl bg-white/5 ${color}`}>
        {icon}
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-white/55">{label}</div>
        <div className="text-lg font-bold">{value}</div>
      </div>
    </GlassCard>
  );
}

function StatsRow({ socket, t }: { socket: any; t: any }) {
  const [latency, setLatency] = useState<number | null>(null);
  const isZh = t?.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);

  useEffect(() => {
    if (!socket) return;
    let done = false;
    const measure = async () => {
      const start = performance.now();
      socket.emit('ping');
      socket.once('pong', () => {
        if (!done) { setLatency(Math.round(performance.now() - start)); done = true; }
      });
    };
    measure();
    const iv = setInterval(measure, 5000);
    return () => { clearInterval(iv); done = true; };
  }, [socket]);

  const cpuCores = (navigator as any).hardwareConcurrency || '?';
  const connected = socket?.connected ?? false;

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <StatCard
        icon={<Cpu size={24} />}
        label={t.computePower || ui('计算能力', 'Compute Power')}
        value={ui(`${cpuCores} 核`, `${cpuCores} Cores`)}
        color="text-celestial-saturn"
      />
      <StatCard
        icon={<Globe size={24} />}
        label={t.nodeSync || ui('节点同步', 'Node Sync')}
        value={connected ? (t.meshActiveLabel || ui('网络已连接', 'Mesh Connected')) : (t.disconnected || ui('未连接', 'Disconnected'))}
        color={connected ? 'text-celestial-mars' : 'text-white/40'}
      />
      <StatCard
        icon={<Zap size={24} />}
        label={t.responseLatency || ui('响应延迟', 'Response Latency')}
        value={latency ? `${latency}ms` : '--'}
        color="text-celestial-glow"
      />
    </div>
  );
}
